import { useState, useCallback, useRef, useEffect } from 'react';
import { ACTION_STATUS } from '../utils/constants';

// Debug flag - only log in development
const DEBUG = import.meta.env.DEV;

/**
 * useActionApproval Hook
 *
 * Manages action approval state and lifecycle for AI-suggested actions.
 * Handles pending → running → completed/skipped/error state transitions.
 *
 * @example
 * ```jsx
 * const { addProposal, runProposal, skipProposal, getProposal } = useActionApproval({
 *   onRun: async (proposal) => {
 *     await updateProfile(proposal.changes);
 *   },
 *   onSkip: (proposal) => {
 *     console.log('Skipped:', proposal.id);
 *   }
 * });
 * ```
 *
 * @param {Object} options - Configuration options
 * @param {Function} options.onRun - Callback when proposal is run (async)
 * @param {Function} options.onSkip - Callback when proposal is skipped
 * @param {Function} options.onComplete - Callback when proposal completes successfully (optional)
 * @param {Function} options.onError - Callback when proposal execution fails (optional)
 * @param {number} options.autoCleanupDelay - Auto-cleanup delay in ms (default: 5000, set to 0 to disable)
 * @returns {Object} { addProposal, runProposal, skipProposal, getProposal }
 */
export const useActionApproval = ({
  onRun,
  onSkip,
  onComplete,
  onError,
  autoCleanupDelay = 5000,
} = {}) => {
  const [proposals, setProposals] = useState([]);
  const cleanupTimersRef = useRef({});
  // Ref to track current proposals - prevents stale closure issues
  const proposalsRef = useRef(proposals);

  // Keep proposalsRef in sync with proposals state
  useEffect(() => {
    proposalsRef.current = proposals;
  }, [proposals]);

  /**
   * Add a new proposal to the list
   */
  const addProposal = useCallback((proposal) => {
    setProposals((prev) => {
      // Check if proposal with same ID already exists
      const exists = prev.some((p) => p.id === proposal.id);
      if (exists) {
        if (DEBUG)
          console.warn(
            `[useActionApproval] Proposal with ID ${proposal.id} already exists`
          );
        return prev;
      }

      // Add new proposal with pending status
      return [
        ...prev,
        {
          ...proposal,
          status: ACTION_STATUS.PENDING,
          createdAt: new Date().toISOString(),
        },
      ];
    });
  }, []);

  /**
   * Update proposal status
   */
  const updateProposalStatus = useCallback(
    (proposalId, status, error = null) => {
      setProposals((prev) =>
        prev.map((p) =>
          p.id === proposalId
            ? {
                ...p,
                status,
                error,
                updatedAt: new Date().toISOString(),
              }
            : p
        )
      );
    },
    []
  );

  /**
   * Schedule auto-cleanup for completed/skipped proposals
   */
  const scheduleCleanup = useCallback(
    (proposalId) => {
      if (autoCleanupDelay <= 0) return;

      // Clear existing timer if any
      if (cleanupTimersRef.current[proposalId]) {
        clearTimeout(cleanupTimersRef.current[proposalId]);
      }

      // Schedule new cleanup
      cleanupTimersRef.current[proposalId] = setTimeout(() => {
        setProposals((prev) => prev.filter((p) => p.id !== proposalId));
        delete cleanupTimersRef.current[proposalId];
      }, autoCleanupDelay);
    },
    [autoCleanupDelay]
  );

  /**
   * Run a proposal (execute the action)
   */
  const runProposal = useCallback(
    async (proposalId) => {
      // Use ref to get current proposals, avoiding stale closure
      const proposal = proposalsRef.current.find((p) => p.id === proposalId);
      if (!proposal) {
        // Always log errors, even in production
        console.error(`[useActionApproval] Proposal ${proposalId} not found`);
        return;
      }

      if (proposal.status !== ACTION_STATUS.PENDING) {
        if (DEBUG) {
          console.warn(
            `[useActionApproval] Proposal ${proposalId} is not in pending state:`,
            proposal.status
          );
        }
        return;
      }

      // Update to running state
      updateProposalStatus(proposalId, ACTION_STATUS.RUNNING);

      try {
        // Execute the run callback
        if (onRun) {
          await onRun(proposal);
        }

        // Update to completed state
        updateProposalStatus(proposalId, ACTION_STATUS.COMPLETED);

        // Call completion callback
        if (onComplete) {
          onComplete(proposal);
        }

        // Schedule cleanup
        scheduleCleanup(proposalId);
      } catch (error) {
        // Always log errors, even in production
        console.error(
          `[useActionApproval] Failed to run proposal ${proposalId}:`,
          error
        );

        // Update to error state
        updateProposalStatus(proposalId, ACTION_STATUS.ERROR, error.message);

        // Call error callback
        if (onError) {
          onError(proposal, error);
        }

        // Schedule cleanup for error state too
        scheduleCleanup(proposalId);
      }
    },
    [onRun, onComplete, onError, updateProposalStatus, scheduleCleanup]
  );

  /**
   * Skip a proposal (reject the action)
   */
  const skipProposal = useCallback(
    (proposalId) => {
      // Use ref to get current proposals, avoiding stale closure
      const proposal = proposalsRef.current.find((p) => p.id === proposalId);
      if (!proposal) {
        // Always log errors, even in production
        console.error(`[useActionApproval] Proposal ${proposalId} not found`);
        return;
      }

      if (proposal.status !== ACTION_STATUS.PENDING) {
        if (DEBUG)
          console.warn(
            `[useActionApproval] Proposal ${proposalId} is not in pending state`
          );
        return;
      }

      // Update to skipped state
      updateProposalStatus(proposalId, ACTION_STATUS.SKIPPED);

      // Call skip callback
      if (onSkip) {
        onSkip(proposal);
      }

      // Schedule cleanup
      scheduleCleanup(proposalId);
    },
    [onSkip, updateProposalStatus, scheduleCleanup]
  );

  /**
   * Get proposal by ID
   */
  const getProposal = useCallback(
    (proposalId) => {
      return proposals.find((p) => p.id === proposalId);
    },
    [proposals]
  );

  /**
   * Cleanup effect: Clear all pending timers on unmount
   * Prevents memory leaks and state updates on unmounted components
   */
  useEffect(() => {
    return () => {
      // Clear all pending timers when component unmounts
      Object.values(cleanupTimersRef.current).forEach((timerId) => {
        clearTimeout(timerId);
      });
      cleanupTimersRef.current = {};
    };
  }, []);

  return {
    // Core methods used by Gigabot
    addProposal,
    runProposal,
    skipProposal,
    getProposal,
  };
};

export default useActionApproval;
